import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { BaiaCard } from "@/components/hospitalization/BaiaCard";
import { NewHospitalizationDialog } from "@/components/hospitalization/NewHospitalizationDialog";
import { useHospitalizationSectors, nextDoseAt } from "@/hooks/useHospitalizationSectors";
import { Settings, Plus, BedDouble, AlertTriangle, Clock } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { useMobileHeader } from "@/contexts/MobileHeaderContext";

export default function HospitalizationDashboard() {
  const { t } = useTranslation("hospitalization");
  useMobileHeader({ title: t("dashboard.title") }, [t]);
  const { data: setores = [], isLoading } = useHospitalizationSectors();
  const [activeSectorId, setActiveSectorId] = useState<string | null>(null);
  const [showNovaDialog, setShowNovaDialog] = useState(false);

  const activeSetor = useMemo(
    () => setores.find((s) => s.id === activeSectorId) ?? setores[0] ?? null,
    [setores, activeSectorId]
  );

  /* ── Global stats ── */
  const allBaias = useMemo(() => setores.flatMap((s) => s.baias), [setores]);
  const allHosp = useMemo(() => allBaias.map((b) => b.hospitalization_ativa).filter(Boolean), [allBaias]);

  const totalOcupadas = allHosp.length;
  const totalBaias = allBaias.length;
  const criticalCount = useMemo(
    () => allHosp.filter((h) => h!.recentRecords.some((r) => r.condicao_geral !== null && r.condicao_geral <= 2)).length,
    [allHosp]
  );

  /* next action across all hospitalizations */
  const nextAction = useMemo(() => {
    let best: { time: Date; petName: string; med: string; baiaName: string } | null = null;
    for (const setor of setores) {
      for (const baia of setor.baias) {
        const h = baia.hospitalization_ativa;
        if (!h) continue;
        for (const p of h.activePrescriptions) {
          const t = nextDoseAt(p);
          if (t && (!best || t < best.time)) {
            best = { time: t, petName: h.pet.name, med: p.medicamento, baiaName: baia.nome };
          }
        }
      }
    }
    return best;
  }, [setores]);

  const nextActionMinutes = nextAction
    ? Math.round((nextAction.time.getTime() - Date.now()) / 60_000)
    : null;

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-10 w-full rounded-full" />
        <div className="grid grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  if (setores.length === 0) {
    return (
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="hidden md:block">
            <h1 className="text-2xl font-bold tracking-tight">{t("dashboard.title")}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t("dashboard.subtitle")}</p>
          </div>
          <Button size="sm" onClick={() => setShowNovaDialog(true)}>
            <Plus className="h-4 w-4 mr-1.5" />{t("dashboard.novaHospitalization")}
          </Button>
        </div>
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
            <BedDouble className="h-7 w-7 text-muted-foreground" />
          </div>
          <div>
            <p className="font-semibold">{t("dashboard.noSectors")}</p>
            <p className="text-sm text-muted-foreground mt-1">{t("dashboard.noSectorsHint")}</p>
          </div>
          <Button asChild variant="outline">
            <Link to="/settings/hospitalization"><Settings className="h-4 w-4 mr-1.5" />{t("dashboard.configurar")}</Link>
          </Button>
        </div>
        <NewHospitalizationDialog open={showNovaDialog} onOpenChange={setShowNovaDialog} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
      {/* Page title */}
      <div className="flex items-center justify-between gap-4">
        <div className="hidden md:block">
          <h1 className="text-2xl font-bold tracking-tight">{t("dashboard.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("dashboard.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button asChild variant="outline" size="sm">
            <Link to="/settings/hospitalization"><Settings className="h-4 w-4 mr-1.5" />{t("settings.title")}</Link>
          </Button>
          <Button size="sm" onClick={() => setShowNovaDialog(true)}>
            <Plus className="h-4 w-4 mr-1.5" />{t("dashboard.novaHospitalization")}
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
        {/* Next action */}
        <div className="px-4 py-3.5 border-r border-border">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">
            <Clock className="h-3 w-3" />
            {nextAction && nextActionMinutes !== null
              ? `${t("dashboard.stats.proximaAcao")} · ${nextActionMinutes <= 0 ? t("dashboard.stats.agora") : `em ${nextActionMinutes} min`}`
              : t("dashboard.stats.proximaAcao")}
          </div>
          {nextAction ? (
            <>
              <p className="text-xl font-bold tracking-tight text-foreground">
                {format(nextAction.time, "HH:mm")}
                <span className="text-sm font-medium text-muted-foreground ml-2">
                  {nextAction.petName} · {nextAction.med}
                </span>
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{nextAction.baiaName}</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t("dashboard.stats.semAcoes")}</p>
          )}
        </div>

        {/* Occupation */}
        <div className="px-4 py-3.5 border-r border-border">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">
            {t("dashboard.stats.ocupacaoTotal")}
          </p>
          <p className="text-xl font-bold tracking-tight">
            {totalOcupadas}/{totalBaias}
            <span className="text-sm font-medium text-muted-foreground ml-2">
              · {totalBaias > 0 ? Math.round((totalOcupadas / totalBaias) * 100) : 0}%
            </span>
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {setores.map((s) => {
              const occ = s.baias.filter((b) => b.hospitalization_ativa).length;
              return `${occ} ${s.nome.toLowerCase()}`;
            }).join(" · ")}
          </p>
        </div>

        {/* Critical */}
        <div className="px-4 py-3.5 border-r border-border">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">
            <AlertTriangle className="h-3 w-3" />
            {t("dashboard.stats.estadoCritico")}
          </div>
          <p className="text-xl font-bold tracking-tight">
            {criticalCount}
            <span className="text-sm font-medium text-muted-foreground ml-2">
              · {t("dashboard.stats.pacientes")}
            </span>
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">
            {allHosp
              .filter((h) => h!.recentRecords.some((r) => r.condicao_geral !== null && r.condicao_geral <= 2))
              .map((h) => h!.pet.name)
              .join(" · ") || "—"}
          </p>
        </div>

        {/* Doses today */}
        <div className="px-4 py-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">
            {t("dashboard.stats.internados")}
          </p>
          <p className="text-xl font-bold tracking-tight">{totalOcupadas}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {allHosp.length > 0
              ? allHosp
                  .slice(0, 2)
                  .map((h) => `${h!.pet.name} (${differenceInDays(new Date(), new Date(h!.entrada_at))}d)`)
                  .join(" · ")
              : "—"}
          </p>
        </div>
      </div>

      {/* Sector tabs */}
      <div className="border-b border-border -mb-1">
        <div className="flex items-end gap-0 overflow-x-auto">
          {setores.map((setor) => {
            const occ = setor.baias.filter((b) => b.hospitalization_ativa).length;
            const total = setor.baias.length;
            const isActive = (activeSectorId ?? setores[0]?.id) === setor.id;
            const hasCritical = setor.baias.some((b) =>
              b.hospitalization_ativa?.recentRecords.some((r) => r.condicao_geral !== null && r.condicao_geral <= 2)
            );
            return (
              <button
                key={setor.id}
                onClick={() => setActiveSectorId(setor.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {setor.nome}
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                  isActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                }`}>
                  {occ}/{total}
                </span>
                {hasCritical && (
                  <span className="w-2 h-2 rounded-full bg-red-500 shadow-sm shadow-red-300" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active sector summary line */}
      {activeSetor && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground -mt-1">
          <span className="font-semibold text-foreground">{activeSetor.nome}</span>
          <span>·</span>
          <span>
            {activeSetor.baias.filter((b) => b.hospitalization_ativa).length} {t("dashboard.stats.ocupadas")},{" "}
            {activeSetor.baias.filter((b) => !b.hospitalization_ativa).length} {t("dashboard.livre").toLowerCase()}
          </span>
          <div className="flex-1 max-w-48 h-1.5 rounded-full bg-muted overflow-hidden">
            <span
              className="block h-full rounded-full bg-gradient-to-r from-amber-400 to-primary transition-all"
              style={{
                width: `${activeSetor.baias.length > 0
                  ? (activeSetor.baias.filter((b) => b.hospitalization_ativa).length / activeSetor.baias.length) * 100
                  : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Bay grid */}
      {activeSetor && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {activeSetor.baias.map((baia) => (
            <BaiaCard key={baia.id} baia={baia} onAdmit={() => setShowNovaDialog(true)} />
          ))}
        </div>
      )}

      <NewHospitalizationDialog open={showNovaDialog} onOpenChange={setShowNovaDialog} />
    </div>
  );
}
